import React, { useEffect, useState } from 'react';
import { 
    Card, 
    Row, 
    Col, 
    Table, 
    Typography, 
    Tag, 
    Space, 
    Button,
    message,
    Spin,
    Modal,
    Input
} from 'antd';
import { 
    UserOutlined, 
    ExclamationCircleOutlined,
    DownloadOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUnpaidStudents } from '../../store/slices/reportsSlice';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { sendWpMessage } from '../../store/slices/wpConfigSlice';
import { message as antdMessage } from 'antd';

const { Text } = Typography;

const Reports = () => {
    const dispatch = useDispatch();
    const { unpaidStudents, loading } = useSelector((state) => state.reports);
    const [msgModalOpen, setMsgModalOpen] = useState(false);
    const [msgLoading, setMsgLoading] = useState(false);
    const [msgText, setMsgText] = useState('');
    const selectedBranch = useSelector((state) => state.branches.selectedBranch);
    const selectedPlace = useSelector((state) => state.branches.selectedPlace);
    // Sayfaya girer girmez raporu çağır
    useEffect(() => {
        dispatch(fetchUnpaidStudents({ branchId: selectedBranch.id, placeId: selectedPlace?.id }));
    }, [dispatch]);

    useEffect(() => {
        if (unpaidStudents && unpaidStudents.length > 0) {
            setMsgText('Sayın veli, çocuğunuzun aidat borcu bulunmaktadır. Lütfen en kısa sürede ödeme yapmanızı rica ederiz. Sağlıklı ve spor dolu günler dileriz!');
        }
    }, [unpaidStudents]);


    const handleSendDebtMsg = async () => {
        setMsgLoading(true);
        try {
            await dispatch(sendWpMessage({
                studentIds: unpaidStudents.map(m => m.studentId),
                message: msgText
            })).unwrap();
            antdMessage.success('Mesaj gönderildi!');
            setMsgModalOpen(false);
        } catch (err) {
            antdMessage.error('Mesaj gönderilemedi!');
        } finally {
            setMsgLoading(false);
        }
    };

    // Excel indirme fonksiyonu
    const handleExportExcel = () => {
        if (!unpaidStudents || unpaidStudents.length === 0) {
            message.warning('İndirilecek veri bulunmamaktadır!');
            return;
        }

        try {
            // Excel için veri hazırla
            const excelData = unpaidStudents.map(student => ({
                'Öğrenci Adı': student.fullName,
                'E-posta': student.email,
                'Telefon': student.phoneNumber,
                'Toplam Borç': student.totalPackagePrice.toLocaleString('tr-TR'),
                'Ödenen Tutar': student.totalPaidAmount.toLocaleString('tr-TR'),
                'Kalan Borç': student.totalRemainingAmount.toLocaleString('tr-TR')
            }));

            // Workbook oluştur
            const ws = XLSX.utils.json_to_sheet(excelData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Borçlu Öğrenciler');

            // Dosya adı oluştur
            const fileName = `borclu_ogrenciler_${dayjs().format('YYYY-MM-DD_HH-mm')}.xlsx`;

            // Excel dosyasını indir
            XLSX.writeFile(wb, fileName);
            message.success('Excel dosyası başarıyla indirildi!');
        } catch (error) {
            console.error('Excel export error:', error);
            message.error('Excel dosyası indirilirken bir hata oluştu!');
        }
    };

    // Toplam borç hesapla
    const totalRemainingAmount = unpaidStudents.reduce((sum, student) => sum + student.totalRemainingAmount, 0);
    const totalStudents = unpaidStudents.length;

    const columns = [
        {
            title: 'Öğrenci Adı',
            dataIndex: 'fullName',
            key: 'fullName',
            width: 150,
            fixed: 'left',
        },
        {
            title: 'E-posta',
            dataIndex: 'email',
            key: 'email',
            width: 200,
            responsive: ['md'],
        },
        {
            title: 'Telefon',
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
            width: 130,
            responsive: ['md'],
        },
        {
            title: 'Toplam Paket',
            dataIndex: 'totalPackages',
            key: 'totalPackages',
            width: 120,
            render: (value) => <Tag color="blue">{value}</Tag>,
        },
        {
            title: 'Toplam Fiyat',
            dataIndex: 'totalPackagePrice',
            key: 'totalPackagePrice',
            width: 130,
            render: (value) => (
                <Text strong style={{ color: '#1890ff' }}>
                    {value.toLocaleString('tr-TR')} ₺
                </Text>
            ),
        },
        {
            title: 'Ödenen Tutar',
            dataIndex: 'totalPaidAmount',
            key: 'totalPaidAmount',
            width: 130,
            render: (value) => (
                <Text style={{ color: '#52c41a' }}>
                    {value.toLocaleString('tr-TR')} ₺
                </Text>
            ),
        },
        {
            title: 'Kalan Borç',
            dataIndex: 'totalRemainingAmount',
            key: 'totalRemainingAmount',
            width: 130,
            render: (value) => (
                <Text strong style={{ color: '#ff4d4f' }}>
                    {value.toLocaleString('tr-TR')} ₺
                </Text>
            ),
        },
        {
            title: 'Kayıt Tarihi',
            dataIndex: 'createdDate',
            key: 'createdDate',
            width: 130,
            responsive: ['md'],
            render: (date) => dayjs(date).format('DD/MM/YYYY'),
        },
    ];

    return (
        <div style={{ padding: '16px' }}>
            
            {/* Özet Kartları */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Space direction="vertical" align="center" style={{ width: '100%' }}>
                            <UserOutlined style={{ fontSize: 24, color: '#f65700' }} />
                            <Text strong>Mesaj Gönder</Text>
                            <Button
                                type="primary"
                                icon={<UserOutlined />}
                                size="small"
                                onClick={() => setMsgModalOpen(true)}
                                disabled={!unpaidStudents.length}
                                style={{ marginBottom: 8, background: '#f65700', borderColor: '#f65700', fontSize: 12, padding: '0 12px', height: 28 }}
                            >
                                Gönder
                            </Button>
                            <Modal
                                open={msgModalOpen}
                                onCancel={() => setMsgModalOpen(false)}
                                onOk={() => {
                                    Modal.confirm({
                                        title: 'Emin misiniz?',
                                        content: 'Bu mesajı listedeki tüm borçlu velilere göndermek istediğinize emin misiniz?',
                                        okText: 'Evet, Gönder',
                                        cancelText: 'Vazgeç',
                                        onOk: handleSendDebtMsg,
                                        confirmLoading: msgLoading,
                                        okButtonProps: { style: { background: '#f65700', borderColor: '#f65700' } }
                                    });
                                }}
                                okText="Gönder"
                                cancelText="Vazgeç"
                                confirmLoading={msgLoading}
                            >
                                <div style={{ marginTop: 24 }}>
                                    <Input.TextArea
                                        rows={4}
                                        value={msgText}
                                        onChange={e => setMsgText(e.target.value)}
                                        placeholder="Mesajınızı yazın..."
                                    />
                                </div>
                            </Modal>
                        </Space>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Space direction="vertical" align="center" style={{ width: '100%' }}>
                            <UserOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                            <Text strong>Borçlu Öğrenci</Text>
                            <Text style={{ fontSize: 18, color: '#1890ff' }}>
                                {totalStudents}
                            </Text>
                        </Space>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Space direction="vertical" align="center" style={{ width: '100%' }}>
                            <ExclamationCircleOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />
                            <Text strong>Toplam Borç</Text>
                            <Text style={{ fontSize: 18, color: '#ff4d4f' }}>
                                {totalRemainingAmount.toLocaleString('tr-TR')} ₺
                            </Text>
                        </Space>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Space direction="vertical" align="center" style={{ width: '100%' }}>
                            <DownloadOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                            <Text strong>Excel İndir</Text>
                            <Button 
                                type="primary" 
                                size="small" 
                                onClick={handleExportExcel}
                                icon={<DownloadOutlined />}
                                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                            >
                                İndir
                            </Button>
                        </Space>
                    </Card>
                </Col>
            </Row>

            {/* Borçlu Öğrenciler Tablosu */}
            <Card 
                title={
                    <Space>
                        <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                        <Text strong>Borçlu Öğrenciler</Text>
                    </Space>
                }
                style={{ marginBottom: 16 }}
            >
                <Table
                    columns={columns}
                    dataSource={unpaidStudents}
                    rowKey="studentId"
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} öğrenci`,
                        responsive: true
                    }}
                    scroll={{ x: 1000 }}
                    loading={loading}
                    locale={{
                        emptyText: loading ? <Spin /> : 'Borçlu öğrenci bulunmamaktadır'
                    }}
                    size="small"
                />
            </Card>
        </div>
    );
};

export default Reports;